import exclamationModal from "@assets/images/exclamationModal.svg";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import DialogCreditBlock from "@saleor/createOrChangeNotRefContract/components/DialogCreditBlock";
import DialogCustomerBlock from "@saleor/createOrChangeNotRefContract/components/DialogCustomerBlock";
import {
  OrderDraftField,
  OrderDraftsQuery,
  useGetCreditLimitLazyQuery,
  useGetCustomerBlockLazyQuery
} from "@saleor/graphql";
import useNotifier from "@saleor/hooks/useNotifier";
import { NextIcon } from "@saleor/macaw-ui";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps, ListSettings, RelayToFlat, SortPage } from "@saleor/types";
import { formatDateTime } from "@saleor/utils/date";
import { getArrowDirection } from "@saleor/utils/sort";
import React, { useRef, useState } from "react";
import { useIntl } from "react-intl";

import useStyle from "./styles";

interface SaveDraftPageProps
  extends Omit<ListProps, "onRowClick">,
    SortPage<OrderDraftField> {
  data?: RelayToFlat<OrderDraftsQuery["orderDrafts"]>;
  settings: ListSettings;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  totalCount: number | null;
  latestPageItemNumber: number | null;
  onRowClick: (id: string, zone: string) => void;
}

const SaveDraftPage: React.FC<SaveDraftPageProps> = ({
  data,
  sort,
  onSort,
  settings,
  onNextLastPage,
  onPreviousFirstPage,
  totalCount,
  latestPageItemNumber,
  pageInfo,
  onPreviousPage,
  onNextPage,
  onUpdateListSettings,
  disabled,
  onRowClick
}) => {
  const classes = useStyle();
  const intl = useIntl();
  const containerRef = useRef();
  const notify = useNotifier();
  const [itemSelected, setItemSelected] = useState(null);

  const [openCustomerBlock, setOpenCustomerBlock] = useState<boolean>(false);
  const [openCreditBlock, setOpenCreditBlock] = useState<boolean>(false);

  const [customerBlockCompleted, setCustomerBlockCompleted] = useState(false);
  const [creditLimitCompleted, setCreditLimitCompleted] = useState(false);

  const [getCustomerBlock] = useGetCustomerBlockLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted(data) {
      if (data.getCustomerBlock.customerBlock) {
        setOpenCreditBlock(false);
        setOpenCustomerBlock(true);
        setCustomerBlockCompleted(false);
      } else {
        setCustomerBlockCompleted(true);
      }
    }
  });

  const [getCreditLimitLazy] = useGetCreditLimitLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted: data => {
      if (data?.getCreditLimit?.creditBlockStatus && !openCustomerBlock) {
        setOpenCustomerBlock(false);
        setOpenCreditBlock(true);
        setCreditLimitCompleted(false);
      } else {
        setCreditLimitCompleted(true);
      }
    },
    onError: error => {
      notify({
        status: "error",
        apiMessage: error.message
      });
    }
  });

  const draftRowHandler = itemSelected => {
    getCustomerBlock({
      variables: {
        input: {
          saleOrganizationCode: itemSelected?.saleOrganization?.code || null,
          soldToCode: itemSelected.customer.code
        }
      }
    });

    getCreditLimitLazy({
      variables: {
        input: {
          salesOrgCode: itemSelected?.saleOrganization?.code,
          soldToCode: itemSelected?.customer?.code
        }
      }
    });
  };

  const onRowClickHandler = () => {
    onRowClick(String(itemSelected.id), itemSelected.type);
  };

  React.useEffect(() => {
    if (customerBlockCompleted && creditLimitCompleted) {
      onRowClickHandler();
    }
  }, [customerBlockCompleted, creditLimitCompleted]);

  return (
    <>
      <Box className={classes.titleHeader}>
        <MyTypography variant="body1" className={classes.titlePage}>
          รายการของฉัน
        </MyTypography>
        <MyTypography variant="body2" style={{ color: "#E41B17" }}>
          (บันทึกฉบับร่างจะเคลียร์ข้อมูลทุกวัน เวลา 24.00 น.)
        </MyTypography>
      </Box>
      <div className={classes.containerTable}>
        <div className={classes.rootTable} ref={containerRef}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCellHeader
                  direction={
                    sort.sort === OrderDraftField.UPDATED_AT
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(OrderDraftField.UPDATED_AT)}
                  data-testid="sort-date-time"
                >
                  {intl.formatMessage({
                    defaultMessage: "Date/Time"
                  })}
                </TableCellHeader>
                <TableCellHeader
                  direction={
                    sort.sort === OrderDraftField.SOLD_TO
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(OrderDraftField.SOLD_TO)}
                >
                  {intl.formatMessage({ defaultMessage: "Sold-to Party" })}
                </TableCellHeader>
                <TableCellHeader
                  direction={
                    sort.sort === OrderDraftField.CONTRACT_NO
                      ? getArrowDirection(sort.asc)
                      : undefined
                  }
                  arrowPosition="right"
                  onClick={() => onSort(OrderDraftField.CONTRACT_NO)}
                  data-testid="sort-contract-no"
                >
                  {intl.formatMessage({
                    defaultMessage: "Contract No. / Pi No."
                  })}
                </TableCellHeader>
                <TableCellHeader>
                  {intl.formatMessage({
                    defaultMessage: "PO No."
                  })}
                </TableCellHeader>
                <TableCellHeader textAlign="center">
                  <MyTypography variant="body3">
                    {intl.formatMessage({
                      defaultMessage: "ดูข้อมูลคำสั่งซื้อ"
                    })}
                  </MyTypography>
                </TableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {data && data.length !== 0 ? (
                renderCollection(data, (item, index) => (
                  <TableRow
                    className={classes.rowTable}
                    key={index}
                    data-testid={`row-table-${index}`}
                    onClick={() => {
                      setItemSelected(item);
                      draftRowHandler(item);
                    }}
                  >
                    <TableCell>
                      <MyTypography variant="body3">
                        {maybe(() => formatDateTime(item.updatedAt))}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {maybe(
                          () => `${item.customer.code} - ${item.customer.name}`
                        )}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography
                        variant="subtitle3"
                        style={{ color: "#009fb8" }}
                      >
                        {maybe(() =>
                          item.contractPiNo ? item.contractPiNo : "-"
                        )}
                      </MyTypography>
                    </TableCell>
                    <TableCell>
                      <MyTypography variant="body3">
                        {maybe(() => (item.poNumber ? item.poNumber : "-"))}
                      </MyTypography>
                    </TableCell>
                    <TableCell align="center">
                      <NextIcon className={classes.arrowIcon} />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow className={classes.rowTable}>
                  <TableCell align="center">
                    ยังไม่มีข้อมูลบันทึกฉบับร่าง
                  </TableCell>
                  <TableCell align="center"></TableCell>
                  <TableCell align="center"></TableCell>
                  <TableCell align="center"></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <StickyHorizontalScrollBar container={containerRef} />
        </div>
        {(data?.length > 0 || disabled) && (
          <Box className={classes.paginateFooter}>
            <TablePaginationCustom
              disabled={disabled}
              settings={settings}
              hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
              onNextPage={onNextPage}
              onPreviousPage={onPreviousPage}
              onPreviousFirstPage={onPreviousFirstPage}
              onNextLastPage={onNextLastPage}
              onUpdateListSettings={onUpdateListSettings}
              hasPreviousPage={
                pageInfo && !disabled ? pageInfo.hasPreviousPage : false
              }
              latestPageItemNumber={latestPageItemNumber}
              totalCount={totalCount || 0}
              isBackFistPage
              isNextLastPage
              isShowIndex
            />
          </Box>
        )}
        <DialogCustomerBlock
          onConfirm={() => {
            setOpenCustomerBlock(false);
          }}
          open={openCustomerBlock}
          okText="ปิดหน้าต่าง"
          title="Customer Blocked"
          content={
            <>
              <img
                className={classes.iconModal}
                src={exclamationModal}
                alt="customer blocked"
              />
              <MyTypography variant="body2" className={classes.inlineBlock}>
                ไม่สามารถดำเนินการได้ เนื่องจากลูกค้ารายนี้ถูก{" "}
                <MyTypography
                  variant="subtitle2"
                  className={classes.inlineBlock}
                >
                  Block{" "}
                </MyTypography>
                กรุณาติดต่อเจ้าหน้าที่สินเชื่อ
              </MyTypography>
            </>
          }
        />
        <DialogCreditBlock
          onConfirm={() => {
            setOpenCreditBlock(false);
          }}
          open={openCreditBlock}
          okText="ปิดหน้าต่าง"
          title="Credit Blocked"
          content={
            <>
              <img
                className={classes.iconModal}
                src={exclamationModal}
                alt="customer blocked"
              />
              <MyTypography variant="body2" className={classes.inlineBlock}>
                ไม่สามารถทำรายการได้ เนื่องจากลูกค้าติด{" "}
                <MyTypography
                  variant="subtitle2"
                  className={classes.inlineBlock}
                >
                  Credit Blocked
                </MyTypography>{" "}
                กรุณาติดต่อเจ้าหน้าที่สินเชื่อ
              </MyTypography>
            </>
          }
        />
      </div>
    </>
  );
};

export default SaveDraftPage;
